import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, BookOpen, Inbox, Target } from "lucide-react";
import { useCustomerAuth } from "../../contexts/CustomerAuthContext";
import { useDocMeta } from "../../lib/useDocMeta";

export default function CustomerDashboard() {
  useDocMeta({ title: "Dashboard · GovBidIQ", path: "/app/dashboard" });
  const { customer, custApi } = useCustomerAuth();
  const [reports, setReports] = useState(null);

  useEffect(() => {
    custApi.get("/customer/reports").then((r) => setReports(r.data)).catch(() => setReports([]));
  }, [custApi]);

  const empty = reports && reports.length === 0;

  return (
    <div className="space-y-6" data-testid="cust-dashboard">
      <header>
        <div className="tick-label text-sky-400 mb-1">/ Dashboard</div>
        <h1 className="font-display font-black text-3xl text-slate-100">Welcome back, {customer?.email?.split("@")[0]}.</h1>
        <p className="text-slate-400 text-sm mt-1">{customer?.email}</p>
      </header>

      {empty ? (
        <div className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-8 text-center" data-testid="cust-dashboard-empty">
          <Inbox className="w-10 h-10 text-slate-600 mx-auto mb-4" strokeWidth={1.5} />
          <div className="font-display font-bold text-slate-100 text-xl mb-2">No pursuits yet</div>
          <p className="text-sm text-slate-400 max-w-sm mx-auto mb-6">Start a new pursuit or claim a shared report and it will appear here.</p>
          <div className="flex flex-wrap gap-2 justify-center">
            <Link to="/app/pursuits/new" className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[11px] tracking-widest uppercase px-3 py-2" data-testid="cust-dashboard-start-pursuit">Start New Pursuit</Link>
            <Link to="/sample" className="border border-sky-500/60 text-sky-300 hover:bg-sky-500/10 font-mono text-[11px] tracking-widest uppercase px-3 py-2">View Sample Report</Link>
            <Link to="/strategy-call" className="border border-sky-500/60 text-sky-300 hover:bg-sky-500/10 font-mono text-[11px] tracking-widest uppercase px-3 py-2">Request Strategy Call</Link>
          </div>
        </div>
      ) : (
        <>
          <section className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5">
            <div className="tick-label text-sky-400 mb-3">/ Recent Pursuits</div>
            <ul className="space-y-2" data-testid="cust-recent">
              {(reports || []).slice(0, 5).map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3">
                  <Link to={`/app/pursuits/${r.id}`} className="text-slate-100 hover:text-sky-300 flex-1 truncate">{r.opportunity_name || r.document_name || r.id}</Link>
                  <span className="font-mono text-[10px] text-slate-500 tracking-widest uppercase shrink-0">{r.final_verdict || r.status}</span>
                </li>
              ))}
            </ul>
            <Link to="/app/pursuits" className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] tracking-widest uppercase text-sky-400">View all pursuits <ArrowRight className="w-3 h-3" /></Link>
          </section>
          <section className="grid sm:grid-cols-3 gap-px bg-[var(--border-soft)]">
            <Quick to="/app/copilot"   icon={Sparkles}  label="Ask Copilot" />
            <Quick to="/app/help"      icon={BookOpen}  label="Learning Hub" />
            <Quick to="/app/pursuits"  icon={Target}    label="All Pursuits" />
          </section>
        </>
      )}
    </div>
  );
}

function Quick({ to, icon: Icon, label }) {
  return (
    <Link to={to} className="bg-[var(--bg-secondary)] p-4 hover:bg-slate-900/70 flex items-center gap-3" data-testid={`cust-quick-${to.replace(/\//g,'-')}`}>
      <Icon className="w-5 h-5 text-sky-400" strokeWidth={1.8} />
      <span className="font-mono text-[11px] tracking-widest uppercase text-slate-200">{label}</span>
    </Link>
  );
}
